import { useSyncExternalStore } from 'react';
import { DESKTOP_OFFERED } from '../features/downloads/systems';
import { withViewTransition } from '../lib/motion';

export type AppView = 'home' | 'write' | 'pricing' | 'desktop' | 'guide';

export const appViewPaths: Readonly<Record<AppView, string>> = {
  home: '/',
  write: '/studio',
  pricing: '/pricing',
  desktop: '/desktop-apps',
  guide: '/how-it-works',
};

// In the navigation's order, then the pages linked from elsewhere, so a change of page can move
// the way the links go.
const viewOrder: readonly AppView[] = ['home', 'write', 'guide', 'pricing', 'desktop'];

const navigationEvent = 'lyrmo:navigate';

function currentView(): AppView {
  switch (window.location.pathname.replace(/\/$/, '')) {
    case appViewPaths.write:
      return 'write';
    case appViewPaths.pricing:
      return 'pricing';
    case appViewPaths.desktop:
      return DESKTOP_OFFERED ? 'desktop' : 'home';
    case appViewPaths.guide:
      return 'guide';
    default:
      return 'home';
  }
}

// The view on the page, to know where back and forward came from: by then the address has changed.
let shownView: AppView | null = null;
let subscribers = 0;

/**
 * How much of the page the sticky header covers as it is, so the page that's leaving slips away
 * beneath the header rather than over it (see layout.css).
 */
function measureHeaderOverlap() {
  const header = document.querySelector('.site-header');
  const main = document.getElementById('main-content');
  const covered =
    header && main ? header.getBoundingClientRect().bottom - main.getBoundingClientRect().top : 0;
  document.documentElement.style.setProperty('--page-covered', `${Math.max(0, covered)}px`);
}

/**
 * Shows the new view as a view transition, moving forward or back along the navigation. `before`
 * changes the address once the page as it was has been captured.
 */
function changeView(from: AppView, to: AppView, before?: () => void) {
  shownView = to;
  const forward = viewOrder.indexOf(to) > viewOrder.indexOf(from);
  const change = () => {
    before?.();
    // The new page's sections come in from the side the navigation went (see layout.css).
    if (from !== to) document.documentElement.dataset.pageEnter = forward ? 'forward' : 'back';
    window.dispatchEvent(new Event(navigationEvent));
  };
  if (from === to) {
    change();
    return;
  }
  measureHeaderOverlap();
  withViewTransition(forward ? 'page-forward' : 'page-back', change);
}

function onPopState() {
  changeView(shownView ?? currentView(), currentView());
}

function subscribe(onChange: () => void) {
  window.addEventListener(navigationEvent, onChange);
  // One listener however many components follow the view, so each change is one transition.
  if (subscribers++ === 0) {
    shownView = currentView();
    window.addEventListener('popstate', onPopState);
  }
  return () => {
    window.removeEventListener(navigationEvent, onChange);
    if (--subscribers === 0) window.removeEventListener('popstate', onPopState);
  };
}

/** Changes views without remounting the editor or losing the draft and undo history. */
export function navigateToView(view: AppView): void {
  const path = appViewPaths[view];
  if (window.location.pathname + window.location.search + window.location.hash === path) return;
  changeView(currentView(), view, () => {
    window.history.pushState(null, '', path);
  });
}

/** Shareable paths with browser back and forward navigation. */
export function useAppView(): AppView {
  return useSyncExternalStore(subscribe, currentView, () => 'write');
}
